import type { MenuOption, SearchOption } from "@dealer-center/core-components";
import type { NavigationMenuItemInterface } from "@dealer-center/ddc-api-commoncontainer-interface";
import { isNavigationMenuItemInterfaceParent } from "@dealer-center/ddc-api-commoncontainer-interface";

export function getSearchOptions(
  linksMenu?: NavigationMenuItemInterface[],
  availableAccounts?: MenuOption[]
) {
  const allNavigationStrings: navigationItem[] =
    extractNavigationLabels(linksMenu);
  const allAccountNames = availableAccounts?.map(option => option.label) ?? [];
  const searchOptions: SearchOption[] = [
    ...allNavigationStrings
      .sort((a, b) =>
        (a?.value.toLowerCase() ?? "").localeCompare(
          b?.value.toLowerCase() ?? ""
        )
      )
      .map(navItem => ({
        label: navItem.label || "",
        value: navItem.value,
        group: "Navigation"
      })),
    // Accounts keep the order they arrive in: favorites first (alphabetical),
    // then account history most-recently-used first. Do not sort them here.
    ...allAccountNames.map(label => ({
      label: label || "",
      value: label,
      group: "Accounts"
    }))
  ];
  return searchOptions;
}
interface navigationItem {
  label: string;
  value: string;
}
function extractNavigationLabels(
  linksMenu: NavigationMenuItemInterface[] | undefined,
  parentLabel = ""
): navigationItem[] {
  return !linksMenu
    ? []
    : linksMenu
        .filter(a => a.linkType !== "header")
        .reduce((acc, item) => {
          const currentLabel = parentLabel
            ? `${parentLabel} > ${item.label}`
            : item.label;
          if (isNavigationMenuItemInterfaceParent(item)) {
            const childrenStrings = item.links.reduce((acc, child) => {
              const childLabel = `${currentLabel} > ${child.label}`;
              if (isNavigationMenuItemInterfaceParent(child)) {
                const grandChildrenStrings = child.links.reduce(
                  (acc, grandChild) => {
                    return acc.concat({
                      label: `${childLabel} > ${grandChild.label}`,
                      value: grandChild.label
                    });
                  },
                  [] as navigationItem[]
                );
                return acc.concat(grandChildrenStrings);
              }
              return acc.concat({
                label: childLabel,
                value: child.label
              } as navigationItem);
            }, [] as navigationItem[]);
            return acc.concat(childrenStrings);
          }
          return acc.concat({ label: currentLabel, value: item.label });
        }, [] as navigationItem[]);
}
export function findMenuItemByLabel(
  menuItems: NavigationMenuItemInterface[],
  label: string
): NavigationMenuItemInterface | null {
  const labels = label.split(" > ");
  return findMenuItemByLabels(menuItems, labels);
}

function findMenuItemByLabels(
  menuItems: NavigationMenuItemInterface[],
  labels: string[]
): NavigationMenuItemInterface | null {
  const [currentLabel, ...remainingLabels] = labels;

  for (const item of menuItems) {
    if (item.label === currentLabel) {
      if (remainingLabels.length === 0) {
        return item;
      }
      if (isNavigationMenuItemInterfaceParent(item)) {
        return findMenuItemByLabels(item.links, remainingLabels);
      }
    }
  }
  return null;
}
